import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../../firebase';
import { getCachedDoc } from '../../utils/docCache';
import { useLanguage } from '../../contexts/LanguageContext';
import { ScrollReveal, StaggerChildren } from '../common/ScrollReveal';
// FaHandshake is already this codebase's sponsor glyph (the admin sponsor tab + the
// SponsorSettings header), so the emblem below borrows meaning instead of inventing it.
import { FaArrowRight, FaHandshake } from 'react-icons/fa';
import { plateStyle, plateTextStyle } from '../../constants/sponsorPlate';

// Full-bleed "Official Sponsor" band, rendered directly below the hero / activity ticker.
//
// Self-contained + read-cheap: ONE cached doc (public/data/site_settings/sponsor) through
// docCache with a 30-min TTL — repeat loads inside that window cost 0 Firestore reads and
// this never becomes a per-visitor live listener (see the site's read-cost history).
// The doc carries the WHOLE sponsor list, so N sponsors still cost exactly 1 read.
//
// It renders NOTHING until an *active* sponsor with a logo exists, so the page never shows
// an empty frame.
//
// LAYOUT MODEL — centred flex-wrap, not a fixed grid. A grid with a hard column count
// (lg:grid-cols-6) puts a lone sponsor in column 1 (left-hugging) and orphans remainders
// against the left edge (7 logos = 6 + 1 sad one on the left). flex-wrap + justify-center
// centres EVERY row including the last, so the composition is axially symmetric at any
// count, and it needs no dynamic class names (which Tailwind's scanner would purge).
// We then cap the row width per count (--sb-row-md/lg) so the wrap breaks where we want:
// 4 logos become 2+2, not 3+1. See bestPerRow().
const SPONSOR_TTL = 30 * 60 * 1000;
const MAX_SPONSORS = 24;          // hard cap — a runaway array must not flood the DOM
const MARQUEE_MIN = 7;            // past this, one scrolling row beats three static ones
const ROW_GAP = 20;               // md:gap-5 in px — the balancing math needs the number
// Content width at the NARROWEST viewport of each breakpoint — the safe value, since a
// cap computed for a wider screen would overflow at the breakpoint's own minimum.
const MD_CONTENT = 720;           // 768 - md px-6 (24*2)
const LG_CONTENT = 960;           // 1024 - lg px-8 (32*2)
const XL_CONTENT = 1152;          // max-w-6xl — the container stops growing here

// Plate geometry per tier. FEWER SPONSORS = BIGGER PLATES, so the band always carries the
// same visual mass and a single sponsor never looks like a chip lost in a wide band.
// `pitch` = md/lg plate width + ROW_GAP; keep it in sync with the width classes above it.
//
// ONLY `stage` (the logo's height box) was enlarged — no plate WIDTH moved, on purpose.
// Uploaded sponsor artwork is typically square-ish, and object-contain then makes the image
// HEIGHT-constrained: the rendered logo is exactly the stage height and the stage's spare
// horizontal room is unreachable. So width buys nothing, while changing a md width would move
// `pitch`, and pitch feeds bestPerRow() and every --sb-row-* cap — i.e. it would silently
// re-wrap existing multi-sponsor layouts. Growth is scaled inversely by count (solo +65%,
// few +42%, many +39%) so the marquee stays a ribbon instead of becoming a wall.
const TIERS = {
    solo: {
        plate: 'w-[300px] sm:w-[360px] md:w-[420px] p-6 sm:p-7',
        stage: 'h-[112px] sm:h-[132px] md:h-[152px]',
        name: 'mt-4 text-lg md:text-xl font-bold text-gray-900',
        display: true,   // site convention: big type gets Space Grotesk via inline style
        pitch: 0,
    },
    few: {
        plate: 'w-[152px] sm:w-[196px] md:w-[224px] p-4 sm:p-5',
        stage: 'h-[64px] sm:h-[76px] md:h-[88px]',
        name: 'mt-3 text-[13px] sm:text-sm font-semibold text-gray-700',
        pitch: 244,
    },
    many: {
        plate: 'w-[136px] sm:w-[156px] md:w-[168px] p-3.5 sm:p-4',
        stage: 'h-[52px] sm:h-[58px] md:h-[64px]',
        name: 'mt-2.5 text-[12px] sm:text-[13px] font-semibold text-gray-600',
        pitch: 188,
    },
};

// Append our UTM tags so the sponsor can attribute the traffic WE send them inside THEIR
// own analytics (Google Analytics etc.). Deliberately paired with rel="noopener sponsored"
// below — we must NOT add rel="noreferrer", which would strip the referrer and make every
// click look like "direct" to the sponsor.
function withUtm(url) {
    try {
        const u = new URL(url);
        if (!u.searchParams.has('utm_source')) u.searchParams.set('utm_source', 'fastlanelv');
        if (!u.searchParams.has('utm_medium')) u.searchParams.set('utm_medium', 'hero_sponsor');
        return u.toString();
    } catch (_) {
        return url; // not an absolute URL — hand it back untouched
    }
}

// How many plates per row so the LAST row is as full as possible, without adding rows.
// Plain wrapping fills greedily and orphans the remainder (7 in 6 columns = 6 + 1);
// this trades a column for a balanced pair of rows (4 + 3). Returns n when it all fits.
function bestPerRow(n, maxPerRow) {
    if (n <= maxPerRow) return n;
    const minRows = Math.ceil(n / maxPerRow);
    let best = maxPerRow;
    let bestOrphan = Infinity;
    for (let p = maxPerRow; p >= 2; p--) {
        if (Math.ceil(n / p) !== minRows) break;   // narrowing further costs a row — stop
        const last = n - (minRows - 1) * p;        // items left for the final row
        if (last < 1) continue;
        if (p - last < bestOrphan) { bestOrphan = p - last; best = p; }
    }
    return best;
}

// Back-compat shim. The live doc is still the ORIGINAL single-sponsor shape
// ({ active, name, url, logoUrl }) and stays valid until an admin re-saves — plus visitors
// hold that old shape in localStorage for up to the 30-min TTL. Without this the paying
// sponsor would vanish from the homepage the moment the new bundle ships.
// `active === false` (not `!active`) mirrors SponsorSettings' own `d.active !== false`
// default, so a doc written without the field still shows.
function normalize(data) {
    if (!data || data.active === false) return [];
    const raw = Array.isArray(data.sponsors) && data.sponsors.length
        ? data.sponsors
        : (data.logoUrl ? [{ logoUrl: data.logoUrl, name: data.name, url: data.url }] : []);
    return raw
        .filter((s) => s && s.logoUrl && s.active !== false)
        .slice(0, MAX_SPONSORS);
}

// `fallbackAlt` is what a nameless sponsor's logo is announced as. It is deliberately NOT the
// band's heading string any more: that string is now a visible <h2>, so reusing it here made a
// screen reader read the same words as the heading and again as the link — and if the logo URL
// 404s, the alt text paints the exact <h2> wording as body text right underneath the <h2>.
function SponsorPlate({ sponsor, tier, fallbackAlt, cta, eager, clone }) {
    const cfg = TIERS[tier];
    const href = sponsor.url ? withUtm(sponsor.url) : null;
    const title = sponsor.displayName;
    const cls = `sb-plate ${href ? 'sb-plate--link ' : ''}h-full flex flex-col items-center justify-center text-center rounded-2xl ${cfg.plate}`;
    // Per-sponsor plate colour (admin: SponsorSettings) — a white logo needs a dark plate.
    const bgStyle = plateStyle(sponsor.plateBg);
    const nameStyle = { ...(cfg.display ? { fontFamily: "'Space Grotesk', sans-serif" } : null), ...plateTextStyle(sponsor.plateBg) };

    const body = (
        <>
            {/* Fixed-height stage + object-contain = every logo gets the same OPTICAL
                weight whatever its aspect ratio (wide wordmark vs square badge). */}
            <div className={`flex items-center justify-center w-full ${cfg.stage}`}>
                <img
                    src={sponsor.logoUrl}
                    alt={title || fallbackAlt}
                    loading={eager ? 'eager' : 'lazy'}
                    decoding="async"
                    draggable="false"
                    // The rounded corner is load-bearing, not decoration: uploaded artwork is
                    // often a SOLID black (or white) rectangle. On a white plate a hard block
                    // reads as a rendering bug; the same block with rounded corners reads as a
                    // deliberate app-icon-style tile. Transparent PNGs are unaffected.
                    // rounded-xl (not -lg) now that the stage is taller: 12px holds roughly the
                    // same radius-to-size ratio the 8px had at the old sizes, and stops short of
                    // the plate's own rounded-2xl, which would read as concentric mush.
                    className="max-h-full w-auto max-w-full object-contain rounded-xl"
                />
            </div>
            {title && (
                <div
                    className={`${cfg.name} w-full truncate`}
                    // Space Grotesk is Latin-only; a Thai sponsor name simply falls through to
                    // the OS sans, which is what the rest of the site already does.
                    style={Object.keys(nameStyle).length ? nameStyle : undefined}
                >
                    {title}
                </div>
            )}
            {cta && (
                <span className="mt-3 inline-flex items-center gap-1.5 text-blue-500 text-sm font-medium">
                    {cta} <FaArrowRight className="text-xs" />
                </span>
            )}
        </>
    );

    if (!href) return <div className={cls} style={bgStyle || undefined}>{body}</div>;

    return (
        // rel is intentionally "noopener sponsored" (NOT noreferrer): noopener already blocks
        // tabnabbing, and keeping the referrer lets the sponsor attribute our traffic.
        // eslint-disable-next-line react/jsx-no-target-blank
        <a
            href={href}
            target="_blank"
            rel="noopener sponsored"
            className={cls}
            style={bgStyle || undefined}
            aria-label={title || fallbackAlt}
            tabIndex={clone ? -1 : undefined}   // marquee duplicates must not be tab stops
        >
            {body}
        </a>
    );
}

function SponsorBand() {
    const { t, lang } = useLanguage();
    const [raw, setRaw] = useState(null);
    const [reduceMotion, setReduceMotion] = useState(false);

    useEffect(() => {
        if (!db) return;
        let mounted = true;
        getCachedDoc('public/data/site_settings/sponsor', SPONSOR_TTL)
            .then((data) => { if (mounted) setRaw(data); })
            .catch(() => { });
        return () => { mounted = false; };
    }, []);

    // Marquee is opt-out for users who asked the OS for less motion — they get the static
    // wrapped rows instead (still symmetric, just taller).
    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return;
        const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
        setReduceMotion(mq.matches);
        const onChange = (e) => setReduceMotion(e.matches);
        if (mq.addEventListener) mq.addEventListener('change', onChange);
        else mq.addListener(onChange);                                  // Safari < 14
        return () => {
            if (mq.removeEventListener) mq.removeEventListener('change', onChange);
            else mq.removeListener(onChange);
        };
    }, []);

    const list = useMemo(
        () => normalize(raw).map((s, i) => ({
            ...s,
            key: s.id || `${s.logoUrl}-${i}`,
            displayName: ((lang === 'en' && s.nameEn) ? s.nameEn : s.name || '').trim(),
        })),
        [raw, lang]
    );

    const count = list.length;
    const tier = count === 1 ? 'solo' : count <= 4 ? 'few' : 'many';
    const marquee = count >= MARQUEE_MIN && !reduceMotion;

    // Row caps that force a balanced wrap at md / lg. Mobile stays uncapped: at 390px the
    // plates fit 2 per row on their own, and a centred 2+1 is still symmetric.
    const rowVars = useMemo(() => {
        const pitch = TIERS[tier].pitch;
        if (!pitch || count < 2) return undefined;
        const fits = (content) => Math.max(1, Math.floor((content + ROW_GAP) / pitch));
        const width = (perRow) => `${perRow * pitch - ROW_GAP + 4}px`;   // +4px slack
        return {
            '--sb-row-md': width(bestPerRow(count, fits(MD_CONTENT))),
            '--sb-row-lg': width(bestPerRow(count, fits(LG_CONTENT))),
            '--sb-row-xl': width(bestPerRow(count, fits(XL_CONTENT))),
        };
    }, [tier, count]);

    if (!count) return null;

    const label = t(count > 1 ? 'hero.sponsorsLabel' : 'hero.sponsorLabel');
    const logoAlt = t('hero.sponsorLogoAlt');
    const loop = marquee ? [...list, ...list] : list;   // duplicated for a seamless loop
    const duration = Math.min(90, Math.max(28, count * 7));

    return (
        <section className="sb-band relative w-full overflow-hidden py-10 sm:py-12 md:py-14">
            <span className="sb-edge sb-edge-t" aria-hidden="true" />
            <span className="sb-glow" aria-hidden="true" />

            <div className="relative z-10 px-4 sm:px-6 lg:px-8">
                <div className="max-w-6xl mx-auto">

                    {/* HEADING — promoted from a caption to a real section title.
                        The old eyebrow spent its whole design budget on micro-typography that
                        Thai cannot use: `uppercase` is a no-op on Thai script, and .14em tracking
                        drags สระ/วรรณยุกต์ away from the consonants they belong to. So the band's
                        most trust-carrying line was also its least readable — 11px slate-400 on
                        #F4F7FC is roughly 2.4:1 contrast, which the eye files as a caption.
                        What makes a heading stand out is SIZE and CONTRAST first and ornament
                        second, so the label is now an <h2> at the exact register every other
                        homepage section uses (HomePage.js:400/426/453, ReviewsSection.js:25) —
                        deliberately NOT larger than those: a paid credit must not out-rank the
                        site's own sections.
                        EMBLEM ABOVE, NOT AN ICON BESIDE THE TEXT: this band's whole premise is a
                        single shared centre axis. HomePage's icon-then-h2 rows are left-aligned;
                        copied here they would shove the h2 off the axis that the rule, the tagline
                        and the plates all sit on. Stacking keeps the symmetry intact.
                        NO pill badge on purpose — every candidate word for one (พันธมิตร /
                        OFFICIAL SPONSOR) is just a synonym of the h2 sitting 40px below it, and
                        the amber pill this site uses elsewhere already means REVIEWS. */}
                    <ScrollReveal animation="fadeUp" duration={650} className="text-center mb-8 sm:mb-10 md:mb-12">
                        <span className="sb-emblem mx-auto mb-4 sm:mb-5" aria-hidden="true">
                            <FaHandshake className="text-blue-500 text-xl md:text-2xl" />
                        </span>
                        <h2
                            className="text-2xl md:text-3xl font-black text-gray-900"
                            // lineHeight is set INLINE, not with a leading-* class, and that is not
                            // laziness: Tailwind's `text-3xl` sets font-size AND line-height, and
                            // the `md:` copy of it lands inside a @media block that is emitted
                            // AFTER the unprefixed utilities. Same specificity, later in the file —
                            // so `md:text-3xl` silently re-sets line-height to 2.25rem and any
                            // plain `leading-*` here is dead above 768px. Verified in the browser.
                            // 1.45 (not the 1.2 that text-3xl imposes) because this string stacks
                            // marks BOTH above and below one cluster (ผ + ู + ้) and also carries
                            // นั นุ ย่ ป็ — the moment a longer label or a 320px screen wraps it,
                            // line 2's วรรณยุกต์ collides with line 1's สระล่าง.
                            // Space Grotesk is Latin-only, so on the Thai label every glyph falls
                            // through to the OS Thai face anyway — it only earns its place on the
                            // English string. Same idiom as SponsorPlate's cfg.display above.
                            style={{
                                lineHeight: 1.45,
                                ...(lang === 'en' ? { fontFamily: "'Space Grotesk', sans-serif" } : null),
                            }}
                        >
                            {label}
                        </h2>
                        {/* The eyebrow's two flanking hairlines, merged into one rule UNDER the
                            title. Ink beside text frames it as an aside; the same ink under it
                            frames it as a title. */}
                        <span className="sb-rule block mx-auto mt-4" aria-hidden="true" />
                        <p className="mt-4 mx-auto max-w-md text-sm sm:text-[15px] leading-relaxed text-gray-500">
                            {t('hero.sponsorTagline')}
                        </p>
                    </ScrollReveal>

                    {tier === 'solo' && (
                        // One sponsor = one large plate on the same centre axis as the label:
                        // logo, name and CTA stacked. Nothing to balance, so nothing can look
                        // lopsided — and the CTA gives the plate enough body to feel intentional.
                        <ScrollReveal animation="fadeUp" duration={700} delay={80} className="flex justify-center">
                            <SponsorPlate
                                sponsor={list[0]}
                                tier="solo"
                                fallbackAlt={logoAlt}
                                cta={list[0].url ? t('hero.sponsorVisit') : null}
                                eager
                            />
                        </ScrollReveal>
                    )}

                    {tier !== 'solo' && !marquee && (
                        // Vars live on the parent because StaggerChildren takes no style prop;
                        // CSS custom properties inherit, so .sb-row still resolves them.
                        <div style={rowVars}>
                            <StaggerChildren
                                animation="fadeUp"
                                staggerDelay={70}
                                duration={650}
                                className="sb-row mx-auto flex flex-wrap items-stretch justify-center gap-3 sm:gap-4 md:gap-5"
                                childClassName="flex-none"
                            >
                                {list.map((s, i) => (
                                    <SponsorPlate key={s.key} sponsor={s} tier={tier} fallbackAlt={logoAlt} eager={i === 0} />
                                ))}
                            </StaggerChildren>
                        </div>
                    )}

                    {marquee && (
                        <ScrollReveal animation="fadeIn" duration={700} delay={80}>
                            {/* Items carry margin-right instead of a flex gap: the track is then
                                exactly 2 x (N x (width + margin)) wide, so translateX(-50%) lands
                                pixel-perfect on the second copy with no seam. */}
                            <div className="sb-marquee relative overflow-hidden">
                                <div className="sb-track flex w-max items-stretch" style={{ '--sb-dur': `${duration}s` }}>
                                    {loop.map((s, i) => {
                                        const clone = i >= list.length;
                                        return (
                                            <div
                                                key={`${s.key}-${i}`}
                                                className="flex-none mr-3 sm:mr-4 md:mr-5"
                                                aria-hidden={clone || undefined}
                                            >
                                                <SponsorPlate
                                                    sponsor={s}
                                                    tier="many"
                                                    fallbackAlt={logoAlt}
                                                    eager={i === 0}
                                                    clone={clone}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </ScrollReveal>
                    )}

                </div>
            </div>

            <span className="sb-edge sb-edge-b" aria-hidden="true" />

            <style>{`
                /* Fades to white at BOTH edges, so the band never hard-clashes with whatever
                   renders above/below (the next section is conditional and may be white,
                   #F8FAFC, or the dark stats card). Symmetric top/bottom by construction. */
                .sb-band{background:linear-gradient(180deg,#FFFFFF 0%,#F4F7FC 16%,#F2F5FB 84%,#FFFFFF 100%)}
                .sb-edge{position:absolute;left:0;right:0;height:1px;z-index:1;background:linear-gradient(90deg,transparent,rgba(15,23,42,.09) 20%,rgba(15,23,42,.09) 80%,transparent)}
                .sb-edge-t{top:0}
                .sb-edge-b{bottom:0}
                .sb-glow{position:absolute;left:50%;top:50%;width:min(1100px,94%);height:300px;transform:translate(-50%,-50%);background:radial-gradient(58% 58% at 50% 50%,rgba(59,130,246,.11),rgba(99,102,241,.05) 46%,rgba(255,255,255,0) 72%);filter:blur(26px);pointer-events:none;z-index:0}

                /* Emblem + rule are the heading's only two ornaments, and both are drawn in the
                   band's OWN two colours (#3B82F6 / #6366F1 — the same pair as .sb-glow above),
                   so the decoration reads as the section's light rather than as stickers dropped
                   on top of it. display:flex (not inline-flex) so mx-auto can centre it.
                   The ::after ring is a second concentric circle, which is what makes a plain
                   icon chip read as a seal/credential — appropriate for an endorsement, and it
                   costs no extra element in the JSX. */
                .sb-emblem{position:relative;display:flex;align-items:center;justify-content:center;width:52px;height:52px;border-radius:9999px;background:linear-gradient(145deg,#FFFFFF 0%,#EEF4FF 100%);border:1px solid #DCE7FA;box-shadow:0 1px 2px rgba(15,23,42,.05),0 14px 28px -18px rgba(37,99,235,.5),inset 0 1px 0 #FFFFFF}
                .sb-emblem::after{content:'';position:absolute;inset:-6px;border-radius:9999px;border:1px solid rgba(59,130,246,.15)}
                @media(min-width:768px){.sb-emblem{width:58px;height:58px}}

                /* The old eyebrow's two flanking hairlines, merged into one centred rule. Faded
                   ends keep it an accent; a full-width line would cut the section in two. */
                .sb-rule{width:64px;height:3px;border-radius:9999px;background:linear-gradient(90deg,rgba(59,130,246,0),#3B82F6 28%,#6366F1 72%,rgba(99,102,241,0))}
                @media(min-width:768px){.sb-rule{width:88px}}

                /* Plate = the repo's card language (white, rounded-2xl, hairline border, lift
                   on hover). It is what makes ANY artwork survive: a white-background JPEG
                   melts into the plate, a dark one reads as an icon on a light surface. */
                .sb-plate{background:#FFFFFF;border:1px solid #E9EEF6;box-shadow:0 1px 2px rgba(15,23,42,.04),0 14px 30px -22px rgba(15,23,42,.45);transition:transform .35s cubic-bezier(0.16,1,0.3,1),box-shadow .35s cubic-bezier(0.16,1,0.3,1),border-color .25s ease}
                .sb-plate--link:hover{transform:translateY(-4px);border-color:#CFE0F8;box-shadow:0 22px 38px -20px rgba(15,23,42,.42),0 4px 12px rgba(15,23,42,.05)}

                .sb-row{max-width:100%}
                @media(min-width:768px){.sb-row{max-width:var(--sb-row-md,100%)}}
                @media(min-width:1024px){.sb-row{max-width:var(--sb-row-lg,100%)}}
                @media(min-width:1280px){.sb-row{max-width:var(--sb-row-xl,100%)}}

                .sb-marquee{-webkit-mask-image:linear-gradient(90deg,transparent,#000 7%,#000 93%,transparent);mask-image:linear-gradient(90deg,transparent,#000 7%,#000 93%,transparent)}
                .sb-track{animation:sbScroll var(--sb-dur,48s) linear infinite}
                .sb-marquee:hover .sb-track,.sb-marquee:focus-within .sb-track{animation-play-state:paused}
                @keyframes sbScroll{from{transform:translateX(0)}to{transform:translateX(-50%)}}

                /* Belt-and-braces: the JS already swaps to static rows, this also kills the
                   animation if that state ever fails to resolve. */
                @media(prefers-reduced-motion:reduce){
                    .sb-track{animation:none}
                    .sb-plate{transition:none}
                    .sb-plate--link:hover{transform:none}
                }
            `}</style>
        </section>
    );
}

export default SponsorBand;
